import useComplexData from '@powerednow/hooks/complexDataHook';
import { complexFindModel, ComplexModelEntity } from '@powerednow/models/modelLookup';
import { DataWrapper } from 'FieldServices/singletons/DataWrapper';
import { AuthManager } from 'FieldServices/singletons/AuthManager';
import Bluebird from 'bluebird';

export function useWorkflow() {
    const { optionalResult } = useComplexData(
        'Company',
        AuthManager.getCompany().get('id')!,
        DataWrapper,
        async (company, resultSetter: (_newResult: {
            loaded: boolean,
            workflows: ComplexModelEntity<'Workflow'>[],
        }) => void) => {
            const workflows = await complexFindModel('Workflow').getAllHighestVersion(company);
            resultSetter({ loaded: true, workflows: workflows.map(workflow => workflow.data) });
        },
    );
    return optionalResult?.workflows;
}

export type CompanyWorkflowWithAssociatedWorkflow = ComplexModelEntity<'CompanyWorkflow'> & {
    workflow: ComplexModelEntity<'Workflow'>,
};

/**
 * This is a hook that will fetch the workflows for the company and return them.
 */
export function useCompanyWorkflow(workflowIdFilter: string) {
    const { optionalResult } = useComplexData(
        'Company',
        [AuthManager.getCompany().get('id')!, workflowIdFilter],
        DataWrapper,
        async (company, resultSetter: (_newResult: {
            loaded: boolean,
            companyWorkflows: CompanyWorkflowWithAssociatedWorkflow[],
        }) => void, watch) => {
            const companyWorkflows = await watch(company.getAllCompanyWorkflow());
            const result = await Bluebird.map(companyWorkflows, async companyWorkflow => ({
                ...companyWorkflow.data.getPureDataValues(),
                workflow: (await companyWorkflow.getWorkflow())?.data.getPureDataValues(),
            }));
            const filteredResults = workflowIdFilter === '' ? result : result.filter(workflow => workflowIdFilter === workflow.workflow.workflowId);
            resultSetter({
                loaded: true,
                companyWorkflows: filteredResults,
            });
        },
    );
    return {
        loaded: optionalResult?.loaded,
        companyWorkflows: optionalResult?.companyWorkflows || [],
    };
}

export function useCompanyWorkflowById(workflowId: number) {
    const { optionalResult } = useComplexData(
        'CompanyWorkflow',
        workflowId,
        DataWrapper,
        async (companyWorkflow, resultSetter: (_newResult: {
            loaded: boolean,
            companyWorkflow: ComplexModelEntity<'Workflow'>,
        }) => void) => {
            const workflow = await companyWorkflow.getWorkflow();
            resultSetter({
                loaded: true,
                companyWorkflow: workflow?.data,
            });
        },
    );
    return {
        loaded: optionalResult?.loaded || true,
        companyWorkflow: optionalResult?.companyWorkflow,
    };
}
